import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PlayCircle, Rocket, StopCircle } from "lucide-react";
import { errorMessage, request } from "../lib/api";
import { track } from "../lib/events";
import { ConfirmSheet } from "../components/ConfirmSheet";
import { FormModal } from "../components/FormModal";
import type { PDDayDetail } from "../types";

type Pending = "start" | "end" | null;

export function SessionControls({
  detail,
  onChanged,
}: {
  detail: PDDayDetail;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [endNote, setEndNote] = useState("");
  // null until answered: ending a PD Day always asks whether the schedule held.
  const [scheduleFollowed, setScheduleFollowed] = useState<boolean | null>(null);
  const [deviation, setDeviation] = useState("");
  const [submittedEndNote, setSubmittedEndNote] = useState<string | null>(null);

  // The backend stores one end note, so a schedule deviation travels as its
  // leading line rather than needing a separate field.
  function composedNote(): string {
    const note = endNote.trim();
    if (scheduleFollowed !== false) return note;
    const line = `${t("scheduleDeviationPrefix")}: ${deviation.trim()}`;
    return note ? `${line}\n${note}` : line;
  }

  async function apply(action: "start" | "end") {
    setBusy(true);
    setError("");
    let recorded = false;
    try {
      await request(`/api/facilitator/pd-days/${detail.id}/${action}`, {
        method: "POST",
        ...(action === "end" ? { body: JSON.stringify({ note: composedNote() }) } : {}),
      });
      recorded = true;
      track(action === "start" ? "pd_day_started" : "pd_day_ended", {
        payload: { session_id: detail.id },
      });
      if (action === "end") {
        setSubmittedEndNote(composedNote());
        setPending(null);
      }
      await onChanged();
      setPending(null);
    } catch (cause) {
      setError(recorded ? t("pdDaySavedRefreshFailed") : errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  // Once the facilitator has ended their log there is nothing left to do on
  // this screen, so the controls (and their confirmation box) go away.
  if (detail.facilitator_status === "ended" || submittedEndNote !== null) return null;

  if (detail.status === "ended") return null;

  const starting = detail.facilitator_status === "scheduled";

  return (
    <section className="flex flex-col gap-2 pt-1">
      <button
        type="button"
        className={`flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold text-content-on-accent shadow-sm transition-opacity active:opacity-90 ${
          starting ? "bg-accent" : "bg-accent-strong"
        }`}
        onClick={() => {
          setError("");
          setPending(starting ? "start" : "end");
        }}
      >
        {starting ? <PlayCircle size={20} /> : <StopCircle size={20} />}
        {starting ? t("startPDDay") : t("endPDDay")}
      </button>
      {error && (
        <p className="rounded-md bg-danger-bg px-3 py-2 text-center text-sm text-accent-strong">
          {error}
        </p>
      )}

      <ConfirmSheet
        open={pending === "start"}
        icon={Rocket}
        tone="secondary"
        placement="center"
        title={t("startPDDayConfirmTitle")}
        message={t("startPDDayConfirmBody", { center: detail.center })}
        confirmLabel={t("startPDDayConfirm")}
        busy={busy}
        onConfirm={() => void apply("start")}
        onCancel={() => setPending(null)}
      />
      <FormModal
        open={pending === "end"}
        title={t("endPDDayConfirmTitle")}
        submitLabel={t("endPDDayConfirm")}
        tone="danger"
        busy={busy}
        disabled={
          scheduleFollowed === null ||
          !endNote.trim() ||
          (scheduleFollowed === false && !deviation.trim())
        }
        error={error}
        onSubmit={() => void apply("end")}
        onCancel={() => setPending(null)}
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-semibold text-app-ink">
            {t("scheduleFollowedLabel")}
          </legend>
          {([true, false] as const).map((followed) => (
            <label
              key={String(followed)}
              className="flex items-center gap-3 rounded-lg border border-app-line px-3 py-2 text-sm text-app-ink"
            >
              <input
                type="radio"
                name="schedule-followed"
                checked={scheduleFollowed === followed}
                onChange={() => setScheduleFollowed(followed)}
                className="accent-accent"
              />
              {followed ? t("scheduleFollowedYes") : t("scheduleFollowedNo")}
            </label>
          ))}
        </fieldset>
        {scheduleFollowed === false && (
          <label className="flex flex-col gap-1 text-sm font-semibold text-app-ink">
            {t("scheduleDeviationLabel")}
            <textarea
              data-autofocus
              required
              maxLength={500}
              rows={3}
              value={deviation}
              onChange={(event) => setDeviation(event.target.value)}
              placeholder={t("scheduleDeviationPlaceholder")}
              className="w-full rounded-xl border border-app-line bg-surface p-3 text-base font-normal text-app-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </label>
        )}
        <label className="text-sm font-medium text-app-ink" htmlFor="pd-day-end-note">
          {t("pdDayEndNoteLabel")}
        </label>
        <textarea
          id="pd-day-end-note"
          required
          maxLength={2000}
          rows={5}
          value={endNote}
          onChange={(event) => setEndNote(event.target.value)}
          className="w-full rounded-xl border border-app-line bg-surface p-3 text-base text-app-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
          placeholder={t("pdDayEndNotePlaceholder")}
        />
      </FormModal>
    </section>
  );
}
